import { SyncOutlined } from '@ant-design/icons'
import { EmptyState, NoDataIcon, RangePicker, Select } from '@lmc/ui-kit'
import type { TablePaginationConfig } from 'antd'
import type { SorterResult } from 'antd/es/table/interface'
import dayjs from 'dayjs'
import useUsersList from 'hooks/useUser/useUsersList'
import { NAMESPACE } from 'locale'
import { dacConstants } from 'locale/deviceAccessControl'
import { type FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import styled, { createGlobalStyle } from 'styled-components'
import { getQueryParams } from 'utils/getQueryParams'
import { Spinner } from '../../../../components/generic'
import { useDeviceSigns, useDevices, useSignTableParams } from '../../hooks'
import { DEVICES_PER_PAGE, type TableParams } from '../../types'
import {
  getCarerOptions,
  getDeviceOptions,
  tableParamsDefaultState
} from '../../utils/devices'
import DevicesSignsTable from './DevicesSignsTable'

export type Values = [dayjs.Dayjs, dayjs.Dayjs]

// The Select dropdowns render in a portal, so their options can't be reached
// from FilterBar's styles — tag them with this class and size them globally.
const SELECT_POPUP_CLASS = 'dac-signs-select-popup'

type DeviceSignsPanelProps = {
  hasRolePermission?: boolean
  /**
   * When true, keeps the browser URL (`/organisation/devices/signs`) in
   * sync with the active filters. Used by the standalone route so deep
   * links/back-navigation keep working. Inline usage (as a DAC tab) omits
   * this so the panel doesn't navigate away from the DAC page.
   */
  syncUrl?: boolean
  testId?: string
}

const DeviceSignsPanel: FC<DeviceSignsPanelProps> = ({
  hasRolePermission = true,
  syncUrl,
  testId
}) => {
  const navigate = useNavigate()

  const { t } = useTranslation(NAMESPACE.DEVICE_ACCESS_CONTROL)

  const { deviceIdParam, userParam, fromParam, toParam } = useSignTableParams()

  const defaultToDate = dayjs()
  const defaultFromDate = dayjs().subtract(180, 'day')

  const [to, setToDate] = useState<dayjs.Dayjs | null>(
    toParam ? dayjs(toParam) : defaultToDate
  )
  const [from, setFromDate] = useState<dayjs.Dayjs | null>(
    fromParam ? dayjs(fromParam) : defaultFromDate
  )
  const [deviceId, setDeviceId] = useState<string | null>(deviceIdParam)

  const [user, setUser] = useState<string | null>(userParam)

  const [tableParams, setTableParams] = useState<TableParams>(
    tableParamsDefaultState()
  )

  const queryParamsForUrl = {
    ...(deviceId && { deviceId }),
    ...(from && { 'date[from]': from?.format('YYYY-MM-DD') }),
    ...(to && { 'date[to]': to?.format('YYYY-MM-DD') }),
    ...(user && { user })
  }

  const queryParamsForRequest = {
    ...queryParamsForUrl,
    'pagination[itemsPerPage]':
      tableParams?.pagination?.pageSize || DEVICES_PER_PAGE,
    'pagination[page]': tableParams?.pagination?.current || 1,
    ...(tableParams?.order && {
      'pagination[sortOrder]': tableParams?.order === 'ascend' ? '1' : '-1'
    }),
    ...(tableParams?.field && {
      'pagination[sortBy]': tableParams?.field
    })
  }

  const { signs, isLoading, count, refetch } = useDeviceSigns(
    queryParamsForRequest,
    hasRolePermission
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync the URL and refetch only when the filter/pagination params change, not on navigate/refetch identity changes
  useEffect(() => {
    if (syncUrl) {
      const options = {
        pathname: '/organisation/devices/signs',
        search: `?${getQueryParams(queryParamsForUrl)}`
      }
      navigate(options, { replace: true })
    }
    refetch()
  }, [from, to, deviceId, user, tableParams])

  const { devices: allDevices } = useDevices(
    undefined,
    hasRolePermission,
    undefined,
    1000
  )

  const { users: allUsers } = useUsersList({
    params: { isDeleted: false, isHidden: false }
  })

  // Keep the pager's total in sync when a fresh count arrives, WITHOUT resetting
  // the current page. Paging fetches a new query key, so `count` briefly goes
  // undefined mid-load — ignore that (and no-op when unchanged) so navigating to
  // a page doesn't bounce back to page one.
  useEffect(() => {
    if (typeof count !== 'number') {
      return
    }
    setTableParams((prev) =>
      prev.pagination?.total === count
        ? prev
        : { ...prev, pagination: { ...prev.pagination, total: count } }
    )
  }, [count])

  // A filter change is a new result set, so jump back to the first page.
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the filters change, not on tableParams identity
  useEffect(() => {
    setTableParams((prev) =>
      prev.pagination?.current === 1
        ? prev
        : { ...prev, pagination: { ...prev.pagination, current: 1 } }
    )
  }, [from, to, deviceId, user])

  const hasSigns = !isLoading && signs?.length

  const handlePaginationChange = (
    pagination: TablePaginationConfig,
    _filters?: unknown,
    sorting?: SorterResult<unknown>
  ) => {
    // Merge rather than replace: page changes come from the pagination footer
    // without a sorter, so keep the existing sort instead of clearing it.
    setTableParams(
      (prev) =>
        ({
          ...prev,
          pagination,
          ...sorting
        }) as TableParams
    )
  }

  const hasActiveFilters =
    !!deviceId ||
    !!user ||
    !from?.isSame(defaultFromDate, 'day') ||
    !to?.isSame(defaultToDate, 'day')

  const resetFilters = () => {
    setFromDate(defaultFromDate)
    setToDate(defaultToDate)
    setDeviceId(null)
    setUser(null)
  }

  if (isLoading) {
    return <Spinner data-testid={testId ? `${testId}-loading` : undefined} />
  }

  return (
    <Wrapper data-testid={testId}>
      <SelectPopupStyles />
      <FilterBar data-testid={testId ? `${testId}-filters` : undefined}>
        <FilterField $basis="26%">
          <FilterLabel>{t(dacConstants.DATE_RANGE)}</FilterLabel>
          <RangePicker
            value={[from, to] as Values}
            onChange={(values) => {
              setFromDate(values?.[0] ? dayjs(values[0]) : null)
              setToDate(values?.[1] ? dayjs(values[1]) : null)
            }}
            format="DD-MM-YYYY"
            allowClear
            style={{ width: '100%' }}
            data-cy="range-picker"
          />
        </FilterField>
        <FilterField $basis="22%">
          <FilterLabel>{t(dacConstants.DEVICE_NAME)}</FilterLabel>
          <Select
            placeholder={t(dacConstants.SELECT_DEVICE_NAME)}
            value={deviceId}
            onChange={(id) => setDeviceId(id as string)}
            options={getDeviceOptions(allDevices)}
            allowClear
            style={{ width: '100%' }}
            popupClassName={SELECT_POPUP_CLASS}
            data-cy="select-device"
          />
        </FilterField>
        <FilterField $basis="28%">
          <FilterLabel>{t(dacConstants.TEAM_MEMBER)}</FilterLabel>
          <Select
            placeholder={t(dacConstants.SELECT_TEAM_MEMBER)}
            value={user}
            onChange={(id) => setUser(id as string)}
            options={getCarerOptions(allUsers)}
            allowClear
            style={{ width: '100%' }}
            popupClassName={SELECT_POPUP_CLASS}
            data-cy="select-team-member"
          />
        </FilterField>
        <FilterField $basis="24%">
          {hasActiveFilters && (
            <ResetButton
              type="button"
              onClick={resetFilters}
              data-testid={testId ? `${testId}-reset` : undefined}
            >
              <SyncOutlined />
              {t(dacConstants.RESET_FILTERS)}
            </ResetButton>
          )}
        </FilterField>
      </FilterBar>
      {hasSigns ? (
        <TableWrapper
          data-testid={testId ? `${testId}-table-wrapper` : undefined}
        >
          <DevicesSignsTable
            signs={signs}
            isLoading={isLoading}
            handleTableChange={handlePaginationChange}
            tableParams={tableParams}
            testId={testId ? `${testId}-table` : undefined}
          />
        </TableWrapper>
      ) : (
        <EmptyState
          title={t(dacConstants.CANT_FIND_ANY)}
          description={
            <span
              data-testid={testId ? `${testId}-empty-description` : undefined}
            >
              {t(dacConstants.WHEN_A_DEVICE)}
            </span>
          }
          icon={<NoDataIcon />}
          testId={testId ? `${testId}-empty` : undefined}
        />
      )}
    </Wrapper>
  )
}

const Wrapper = styled.div`
  width: 100%;
`

const TableWrapper = styled.div`
  overflow-y: auto;
`

const FilterBar = styled.div`
  background-color: ${({ theme }) => theme.color.neutral.color10};
  display: flex;
  align-items: flex-end;
  gap: 16px;
  padding: 8px 24px;

  /* Match the 16px filter labels and table body text. */
  .ant-select-selector,
  .ant-select-selection-item,
  .ant-select-selection-placeholder,
  .ant-select-selection-search-input,
  .ant-picker-input > input {
    font-size: 16px;
  }

  /* The 16px input font pushes the RangePicker taller than the 32px Selects;
     pin it back to the shared control height. */
  .ant-picker {
    height: 32px;
  }
`

// Portaled Select dropdowns (see SELECT_POPUP_CLASS) — keep the option text in
// step with the 16px fields.
const SelectPopupStyles = createGlobalStyle`
  .${SELECT_POPUP_CLASS} .ant-select-item {
    font-size: 16px;
  }
`

/* Each field tracks the proportion of its table column so the filters line
   up with the columns below. min-width: 0 lets them shrink past their content
   so the four columns (incl. Reset filters) absorb the bar's gaps and stay on
   one row instead of wrapping. */
const FilterField = styled.div<{ $basis: string }>`
  flex: 0 1 ${({ $basis }) => $basis};
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FilterLabel = styled.label`
  font-size: 14px;
  line-height: 16px;
  color: ${({ theme }) => theme.color.neutral.color4};
`

const ResetButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-weight: 500;
  color: ${({ theme }) => theme.color.status.primary};
`

export default DeviceSignsPanel
